<template>
  <div
    class="modal fade"
    id="salesReturnDetailsModal"
    tabindex="-1"
    aria-labelledby="salesReturnDetailsModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content sales-return-details-modal">
        <div class="modal-header border-0 pb-0">
          <div>
            <span class="status-badge" :class="`status-${statusValue}`">
              {{ formatStatus(salesReturn?.status) }}
            </span>
            <h4 class="modal-title text-title mt-3 mb-2" id="salesReturnDetailsModalLabel">
              {{ $t("sales_return_page.sales_return_label") }} {{ salesReturn?.return_ref || "" }}
            </h4>
            <p class="text-muted mb-0">
              {{ salesReturn?.reference_no || salesReturn?.sale?.reference_no || $t("sales_return_page.not_available_short") }} •
              {{ formatDateTime(salesReturn?.created_at) }}
            </p>
          </div>
          <button
            type="button"
            class="btn-close p-0"
            data-bs-dismiss="modal"
            :aria-label="$t('common.close')"
          >
            <img src="../../../assets/img/icons/close-circle-2.svg" :alt="$t('common.close')" />
          </button>
        </div>

        <div v-if="salesReturn" class="modal-body">
          <div class="row g-3 mb-4">
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_return_page.customer") }}</span>
                <strong class="summary-value">{{ salesReturn.customer_name || salesReturn.customer?.name || $t("sales_return_page.not_available_short") }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_return_page.warehouse") }}</span>
                <strong class="summary-value">{{ salesReturn.warehouse_name || salesReturn.warehouse?.name || $t("sales_return_page.not_available_short") }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_return_page.sale_reference") }}</span>
                <strong class="summary-value">{{ salesReturn.reference_no || salesReturn.sale?.reference_no || $t("sales_return_page.not_available_short") }}</strong>
              </div>
            </div>
            <div class="col-md-3 col-6">
              <div class="summary-card">
                <span class="summary-label">{{ $t("sales_return_page.grand_total") }}</span>
                <strong class="summary-value">${{ Number(salesReturn.grand_total || 0).toFixed(2) }}</strong>
              </div>
            </div>
          </div>

          <div class="details-section mb-4">
            <div class="section-header">
              <div>
                <h6 class="section-title mb-1">{{ $t("sales_return_page.returned_items") }}</h6>
                <p class="section-subtitle mb-0">{{ $t("sales_return_page.review_returned_items") }}</p>
              </div>
            </div>

            <div class="table-responsive sales-return-items-table">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("sales_return_page.product") }}</th>
                    <th>{{ $t("sales_return_page.code") }}</th>
                    <th class="text-center">{{ $t("sales_return_page.qty") }}</th>
                    <th class="text-end">{{ $t("sales_return_page.unit_price") }}</th>
                    <th class="text-end">{{ $t("sales_return_page.subtotal") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in salesReturn.items" :key="`${item.product_id}-${index}`">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.product_name || item.product?.name || $t("sales_return_page.not_available_short") }}</td>
                    <td>{{ item.product_code || item.product?.code || $t("sales_return_page.not_available_short") }}</td>
                    <td class="text-center">{{ item.quantity }}</td>
                    <td class="text-end">${{ Number(item.unit_price || 0).toFixed(2) }}</td>
                    <td class="text-end">${{ Number(item.subtotal || item.total || 0).toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="details-section">
            <div class="row g-3">
              <div class="col-md-8">
                <h6 class="section-title mb-2">{{ $t("sales_return_page.notes") }}</h6>
                <p class="note-block mb-0">{{ salesReturn.note || $t("sales_return_page.no_notes_added") }}</p>
              </div>
              <div class="col-md-4">
                <div class="totals-card">
                  <div class="total-row">
                    <span>{{ $t("sales_return_page.subtotal") }}</span>
                    <strong>${{ Number(salesReturn.total || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("sales_return_page.discount") }}</span>
                    <strong>${{ Number(salesReturn.discount || salesReturn.total_discount || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("sales_return_page.tax") }}</span>
                    <strong>${{ Number(salesReturn.tax || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row">
                    <span>{{ $t("sales_return_page.shipping") }}</span>
                    <strong>${{ Number(salesReturn.shipping || 0).toFixed(2) }}</strong>
                  </div>
                  <div class="total-row grand">
                    <span>{{ $t("sales_return_page.grand_total") }}</span>
                    <strong>${{ Number(salesReturn.grand_total || 0).toFixed(2) }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">{{ $t("common.close") }}</button>
          <button type="button" class="btn style-five upload-btn px-xxl-6" @click="printSalesReturn">
            {{ $t("sales_return_page.print") }}
            <img src="../../../assets/img/icons/download.svg" :alt="$t('sales_return_page.print')" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import type { SalesReturnRecord } from "@/services/salesReturn";

export default defineComponent({
  name: "SalesReturnDetails",
  props: {
    salesReturn: {
      type: Object as PropType<SalesReturnRecord | null>,
      default: null,
    },
  },
  setup(props) {
    const { t, locale } = useI18n();
    const statusValue = computed(() => String(props.salesReturn?.status || "completed").toLowerCase());

    const formatStatus = (value?: string) => {
      const normalized = String(value || "completed").toLowerCase();
      const statusKeyMap: Record<string, string> = {
        completed: "sales_return_page.status_completed",
        pending: "sales_return_page.status_pending",
      };

      return statusKeyMap[normalized]
        ? t(statusKeyMap[normalized])
        : normalized.charAt(0).toUpperCase() + normalized.slice(1);
    };

    const formatDateTime = (value?: string) => {
      if (!value) return t("sales_return_page.not_available_short");
      return new Date(value).toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const printSalesReturn = () => {
      if (!props.salesReturn) {
        Swal.fire(t("sales_return_page.error"), t("sales_return_page.no_sales_return_selected_to_print"), "error");
        return;
      }

      const rows = (props.salesReturn.items || [])
        .map(
          (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${item.product_name || item.product?.name || t("sales_return_page.not_available_short")}</td>
              <td>${item.product_code || item.product?.code || t("sales_return_page.not_available_short")}</td>
              <td style="text-align:center;">${item.quantity}</td>
              <td style="text-align:right;">$${Number(item.unit_price || 0).toFixed(2)}</td>
              <td style="text-align:right;">$${Number(item.subtotal || item.total || 0).toFixed(2)}</td>
            </tr>
          `
        )
        .join("");

      const printMarkup = `
        <html>
          <head>
            <title>${t("sales_return_page.sales_return_label")} ${props.salesReturn.return_ref}</title>
            <style>
              @page { size: auto; margin: 14mm; }
              body { font-family: Arial, sans-serif; padding: 32px; color: #1f2940; }
              h1 { margin: 0 0 8px; font-size: 28px; }
              .muted { color: #6b7280; font-size: 14px; }
              .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 24px 0; }
              .card { border: 1px solid #e6ebf2; border-radius: 12px; padding: 16px; }
              .label { display: block; color: #6b7280; font-size: 12px; margin-bottom: 8px; }
              .value { font-size: 18px; font-weight: 700; }
              table { width: 100%; border-collapse: collapse; margin-top: 8px; }
              th, td { border-bottom: 1px solid #e6ebf2; padding: 12px 10px; font-size: 14px; }
              th { text-align: left; background: #f8fafc; }
              .totals { margin-top: 24px; width: 320px; margin-left: auto; }
              .total-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e6ebf2; }
              .grand { font-size: 18px; font-weight: 700; }
            </style>
          </head>
          <body>
            <h1>${t("sales_return_page.sales_return_label")} ${props.salesReturn.return_ref}</h1>
            <div class="muted">${t("sales_return_page.original_sale_label")}: ${props.salesReturn.reference_no || props.salesReturn.sale?.reference_no || t("sales_return_page.not_available_short")}</div>
            <div class="muted">${formatDateTime(props.salesReturn.created_at)}</div>

            <div class="summary">
              <div class="card"><span class="label">${t("sales_return_page.customer")}</span><div class="value">${props.salesReturn.customer_name || props.salesReturn.customer?.name || t("sales_return_page.not_available_short")}</div></div>
              <div class="card"><span class="label">${t("sales_return_page.warehouse")}</span><div class="value">${props.salesReturn.warehouse_name || props.salesReturn.warehouse?.name || t("sales_return_page.not_available_short")}</div></div>
              <div class="card"><span class="label">${t("sales_return_page.status")}</span><div class="value">${formatStatus(props.salesReturn.status)}</div></div>
              <div class="card"><span class="label">${t("sales_return_page.grand_total")}</span><div class="value">$${Number(props.salesReturn.grand_total || 0).toFixed(2)}</div></div>
            </div>

            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>${t("sales_return_page.product")}</th>
                  <th>${t("sales_return_page.code")}</th>
                  <th style="text-align:center;">${t("sales_return_page.qty")}</th>
                  <th style="text-align:right;">${t("sales_return_page.unit_price")}</th>
                  <th style="text-align:right;">${t("sales_return_page.subtotal")}</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>

            <div class="totals">
              <div class="total-row"><span>${t("sales_return_page.subtotal")}</span><strong>$${Number(props.salesReturn.total || 0).toFixed(2)}</strong></div>
              <div class="total-row"><span>${t("sales_return_page.discount")}</span><strong>$${Number(props.salesReturn.discount || props.salesReturn.total_discount || 0).toFixed(2)}</strong></div>
              <div class="total-row"><span>${t("sales_return_page.tax")}</span><strong>$${Number(props.salesReturn.tax || 0).toFixed(2)}</strong></div>
              <div class="total-row"><span>${t("sales_return_page.shipping")}</span><strong>$${Number(props.salesReturn.shipping || 0).toFixed(2)}</strong></div>
              <div class="total-row grand"><span>${t("sales_return_page.grand_total")}</span><strong>$${Number(props.salesReturn.grand_total || 0).toFixed(2)}</strong></div>
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const frameWindow = iframe.contentWindow;
      const frameDocument = iframe.contentDocument || frameWindow?.document;

      if (!frameWindow || !frameDocument) {
        document.body.removeChild(iframe);
        Swal.fire(t("sales_return_page.print_failed"), t("sales_return_page.unable_to_prepare_sales_return_print"), "error");
        return;
      }

      frameDocument.open();
      frameDocument.write(printMarkup);
      frameDocument.close();

      let printed = false;
      const cleanup = () => {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 300);
      };

      const trigger = () => {
        if (printed) return;
        printed = true;
        frameWindow.focus();
        frameWindow.print();
        frameWindow.onafterprint = cleanup;
      };

      iframe.onload = () => setTimeout(trigger, 250);
      setTimeout(trigger, 600);
    };

    return {
      statusValue,
      formatStatus,
      formatDateTime,
      printSalesReturn,
    };
  },
});
</script>

<style scoped>
.sales-return-details-modal {
  border-radius: 24px;
  padding: 8px;
}

.summary-card,
.totals-card {
  border: 1px solid #e6ebf2;
  border-radius: 18px;
  background: #fff;
  padding: 18px;
  height: 100%;
}

.summary-label {
  display: block;
  color: #7a8699;
  font-size: 13px;
  margin-bottom: 8px;
}

.summary-value {
  font-size: 18px;
  color: #1f2940;
}

.details-section {
  border: 1px solid #edf2f7;
  border-radius: 18px;
  padding: 20px;
  background: #fff;
}

.section-title {
  font-size: 18px;
  color: #1f2940;
}

.section-subtitle,
.note-block {
  color: #6b7280;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}

.total-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #edf2f7;
}

.total-row.grand {
  border-bottom: 0;
  padding-bottom: 0;
  color: #5d5fef;
  font-size: 18px;
}
</style>
